import React, { useCallback } from 'react';
import {
	Draggable,
	DraggableProvided,
	DraggableStateSnapshot,
} from 'react-beautiful-dnd';

import styled, { useTheme } from 'styled-components';

const DraggableContent = styled.div`
	.drag-icon {
		cursor: grab;
	}
`;

export type DraggableContainerProps = {
	children:
		| React.ReactNode
		| ((
				provided: DraggableProvided,
				snapshot: DraggableStateSnapshot,
		  ) => React.ReactNode);
	draggableId: string;
	index: number;
	className?: string;
	style?: React.CSSProperties;
	itemDraggingStyle?: React.CSSProperties;
	/**
	 * If true, dragHandleProps will NOT be automatically spread on the container.
	 * Use this when you want to manually apply dragHandleProps to a specific element
	 * by using children as a render function.
	 */
	customDragHandle?: boolean;
};

export default function DraggableContainer({
	children,
	draggableId,
	index,
	className,
	style,
	itemDraggingStyle,
	customDragHandle = false,
}: DraggableContainerProps) {
	const theme = useTheme();

	const getItemStyle = useCallback(
		(isDragging: any, draggableStyle: any) => ({
			// some basic styles to make the items look a bit nicer
			userSelect: 'none',
			cursor: 'default',
			marginBottom: theme.spacing?.medium || 'none',

			// styles we need to apply on draggables
			...draggableStyle,
			// overide initial draggable styles
			...style,

			// external styles to apply on draggables when dragging
			...(isDragging && itemDraggingStyle),
		}),
		[itemDraggingStyle],
	);

	return (
		<Draggable draggableId={draggableId} index={index}>
			{(provided, snapshot) => (
				<DraggableContent
					ref={provided.innerRef}
					{...provided.draggableProps}
					{...(!customDragHandle && provided.dragHandleProps)}
					className={className}
					style={getItemStyle(
						snapshot.isDragging,
						provided.draggableProps.style,
					)}
				>
					{typeof children === 'function'
						? children(provided, snapshot)
						: children}
				</DraggableContent>
			)}
		</Draggable>
	);
}
